import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';

/**
 * Sélecteur d'offre — remplace Kaching Bundles.
 *
 * Le Liquid pose la structure, le JS calcule les prix : ils suivent ainsi la variante
 * choisie par le client, ce qu'un rendu figé côté serveur ne permet pas.
 *
 * On écrit dans le formulaire produit d'Horizon plutôt que de le court-circuiter :
 * le fly-to-cart, le cart drawer et le tracking continuent de fonctionner.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement[]} rows
 * @property {HTMLInputElement[]} inputs
 * @property {HTMLElement[]} prices
 * @property {HTMLElement[]} compares
 * @property {HTMLElement[]} savings
 *
 * @extends {Component<Refs>}
 */
export class OfferSelectorComponent extends Component {
  requiredRefs = ['rows'];

  /** @type {Map<string, {price: number, compare: number}>} */
  #prices = new Map();

  connectedCallback() {
    super.connectedCallback();
    this.#parsePrices();

    // Si l'éditeur n'a coché aucune offre par défaut, on prend la première.
    if (!this.#checkedIndex() && this.refs.inputs?.[0]) {
      this.refs.inputs[0].checked = true;
    }

    this.#render();
    this.#apply();
    this.#watchVariant();
    this.#watchSubmit();
  }

  /** Appelé par `on:change="/select"` sur chaque radio. */
  select() {
    this.#apply();
  }

  get #form() {
    return document.getElementById(this.dataset.formId ?? '');
  }

  get #currentVariantId() {
    const fromForm = this.#form?.querySelector('[name="id"]');
    return fromForm?.value || this.dataset.defaultVariant || '';
  }

  get #unit() {
    return this.#prices.get(this.#currentVariantId) ?? this.#prices.values().next().value ?? { price: 0, compare: 0 };
  }

  #parsePrices() {
    for (const entry of (this.dataset.variantPrices ?? '').split(',')) {
      const [id, price, compare] = entry.split(':');
      if (id) this.#prices.set(id.trim(), { price: Number(price) || 0, compare: Number(compare) || 0 });
    }
  }

  #money(cents) {
    return formatMoney(cents, this.dataset.moneyFormat, this.dataset.currency);
  }

  #checkedIndex() {
    return (this.refs.inputs ?? []).findIndex((input) => input.checked) + 1;
  }

  get #selectedRow() {
    const index = this.#checkedIndex();
    return index ? this.refs.rows[index - 1] : this.refs.rows[0];
  }

  /** Recalcule et réécrit les prix de chaque offre. */
  #render() {
    const unit = this.#unit;

    this.refs.rows.forEach((row, index) => {
      const quantity = Number(row.dataset.quantity) || 1;
      const total = unit.price * quantity;

      // Prix barré : saisi dans l'éditeur, sinon dérivé du prix barré de la variante.
      const manual = Number(row.dataset.compareTotal) || 0;
      const compare = manual > 0 ? manual : (unit.compare > unit.price ? unit.compare * quantity : 0);
      const saving = compare > total ? compare - total : 0;

      const priceEl = this.refs.prices?.[index];
      if (priceEl) priceEl.textContent = this.#money(total);

      const compareEl = this.refs.compares?.[index];
      if (compareEl) {
        compareEl.textContent = saving ? this.#money(compare) : '';
        compareEl.hidden = !saving;
      }

      const savingEl = this.refs.savings?.[index];
      if (savingEl) {
        const show = saving > 0 && row.dataset.showSaving === 'true';
        if (show) {
          savingEl.textContent =
            row.dataset.savingFormat === 'percent'
              ? `-${Math.round((saving / compare) * 100)}%`
              : `${row.dataset.savingPrefix ?? ''} ${this.#money(saving)}`.trim();
        }
        savingEl.hidden = !show;
      }
    });
  }

  /** Écrit l'offre sélectionnée dans le formulaire produit. */
  #apply() {
    const row = this.#selectedRow;
    if (!row) return;

    for (const candidate of this.refs.rows) {
      candidate.classList.toggle('offer-row--selected', candidate === row);
    }

    const quantity = this.#form?.querySelector('[name="quantity"]');
    if (quantity instanceof HTMLInputElement) {
      quantity.value = row.dataset.quantity || '1';
      quantity.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (this.dataset.updatePrice === 'true') this.#updatePagePrice(row);

    // Les cadeaux à débloquer écoutent cet événement pour s'ouvrir.
    document.dispatchEvent(
      new CustomEvent('offer:change', {
        detail: {
          quantity: Number(row.dataset.quantity) || 1,
          index: this.refs.rows.indexOf(row),
        },
      })
    );
  }

  /** Reflète le total de l'offre dans le prix affiché en haut de page. */
  #updatePagePrice(row) {
    const index = this.refs.rows.indexOf(row);
    const source = this.refs.prices?.[index];
    const target = document.querySelector('.price__container .price, product-price .price');
    if (target && source?.textContent) target.textContent = source.textContent;
  }

  /** Le client change de variante : on recalcule tout. */
  #watchVariant() {
    const input = this.#form?.querySelector('[name="id"]');
    if (!input) return;

    new MutationObserver(() => {
      this.#render();
      this.#apply();
    }).observe(input, { attributes: true, attributeFilter: ['value'] });

    input.addEventListener('change', () => {
      this.#render();
      this.#apply();
    });
  }

  /**
   * Applique le code de réduction de l'offre choisie.
   * Shopify le mémorise pour la session : il sera actif au checkout.
   */
  #applyDiscount(row) {
    const code = row?.dataset.discountCode?.trim();
    if (!code) return;

    fetch(`${window.Shopify?.routes?.root ?? '/'}discount/${encodeURIComponent(code)}`, {
      method: 'GET',
      redirect: 'follow',
    }).catch(() => {
      // Un code refusé ne doit jamais bloquer l'ajout au panier.
    });
  }

  /**
   * Ajoute le cadeau et applique la réduction avant que le formulaire ne parte,
   * puis laisse Horizon poursuivre normalement — on n'intercepte pas la soumission.
   */
  #watchSubmit() {
    this.#form?.addEventListener(
      'submit',
      () => {
        const row = this.#selectedRow;
        this.#applyDiscount(row);

        const giftId = row?.dataset.giftVariant;
        if (!giftId) return;

        fetch(`${window.Shopify?.routes?.root ?? '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [
              {
                id: Number(giftId),
                quantity: Number(row.dataset.giftQuantity || 1),
                properties: { _offert: 'true' },
              },
            ],
          }),
        }).catch(() => {
          // Un cadeau qui ne part pas ne doit jamais bloquer la vente.
        });
      },
      { capture: true }
    );
  }
}

if (!customElements.get('offer-selector-component')) {
  customElements.define('offer-selector-component', OfferSelectorComponent);
}
